import React, { FC, useEffect, useRef, useState } from 'react';
import './style.scss';
import RepTripHistoryCard from './rep-trip-history-card';
import { apiService } from 'utils/api';
import { useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { STORAGE_KEYS } from 'utils/constants';

const ALL_TABS = {
  ALL: 'All',
  COMPLETED_TRIPS: 'Completed Trips',
  FAILED_TRIPS: 'Failed Trips',
  RETURN_TRIPS: 'Return Trips'
} as const;

type InboundFilterType = (typeof ALL_TABS)[keyof typeof ALL_TABS];

const navStyles = {
  active: 'border-b border-b-[4px] border-b-[#E60A2B] mb-0 text-[#1A1A1A]',
  inactive: 'text-[#858585]'
};

type Props = {
  filter: {
    startDate: Date;
    endDate: Date;
  };
  activeTab: InboundFilterType;
  setActiveTab: (tab: InboundFilterType) => void;
};

const TripHistory: FC<Props> = ({ filter, activeTab, setActiveTab }) => {
  const isMounted = useRef(false);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [trips, setTrips] = useState([]);
  const [allTripsCount, setAllTripsCount] = useState(0);
  const [completedTripsCount, setCompletedTripsCount] = useState(0);
  const [failedTripsCount, setFailedTripsCount] = useState(0);
  const [averageDeliveryTime, setAverageDeliveryTime] = useState(0);
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);
  useEffect(() => {
    getTrips(true);
  }, []);

  const getUrl = () => {
    const tab = activeTab;

    switch (tab) {
      case ALL_TABS.COMPLETED_TRIPS:
        return 'cs/assigned/completed/trips';
      case ALL_TABS.FAILED_TRIPS:
        return 'cs/assigned/failed/trips';
      case ALL_TABS.RETURN_TRIPS:
        return 'cs/assigned/return/trips';
      default:
        return 'cs/assigned/all/trips';
    }
  };

  const getTrips = async (isNew?: boolean) => {
    if (isNew) setLoading(true);
    const { startDate: start_date, endDate: end_date } = filter;
    const page = isNew ? 1 : pageNumber;
    const params = {
      page,
      start_date,
      end_date,
      cokitchen_id: cokitchenId
    };

    let allTrips = isNew ? [] : trips;
    let totalPages = 0;
    try {
      if (activeTab === ALL_TABS.RETURN_TRIPS || activeTab === ALL_TABS.ALL) {
        const res = await apiService.get(
          `cs/assigned/${activeTab === ALL_TABS.RETURN_TRIPS ? 'return' : 'all'}/trips/${id}`,
          {
            params,
            baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
          }
        );
        allTrips = isNew ? res.data.data : [...trips, ...res.data.data];
        totalPages = res.data.pagination.total;
      } else {
        const res = await apiService.get(`${getUrl()}/${id}`, { params });

        allTrips = isNew ? res.data.results : [...trips, ...res.data.results];
        totalPages = res.data.total;
      }

      setTrips(allTrips);
      setTotalPages(totalPages);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };
  useEffect(() => {
    getStats();
  }, [trips]);

  const getStats = async () => {
    const { startDate: start_date, endDate: end_date } = filter;
    const params = {
      start_date,
      end_date,
      cokitchen_id: cokitchenId
    };
    const res = await apiService.get(`cs/assigned/stats/trips/${id}`, {
      params,
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });
    setAllTripsCount(res.data.completed_trips + res.data.failed_trips);
    setCompletedTripsCount(res.data.completed_trips);
    setFailedTripsCount(res.data.failed_trips);
    setAverageDeliveryTime(res.data.average_delivery_time ?? 0);
  };

  useEffect(() => {
    if (pageNumber > 1) getTrips();
  }, [pageNumber]);

  useEffect(() => {
    if (isMounted.current) {
      setHasMore(false);
      setPageNumber(1);
      getTrips(true);
    }
  }, [activeTab, filter]);
  useEffect(() => {
    setHasMore(trips?.length < totalPages);
  }, [trips]);

  const fetchMoreData = () => {
    setPageNumber((p) => p + 1);
  };

  return (
    <div className='reps_info'>
      <div className='mt-8 bg-[#F7F8F8] w-full max-w-[873px] h-[144px] p-1 flex justify-center items-center rounded-[8px] '>
        <div className='bg-white w-full max-w-[865px] h-[136px] rounded-md flex justify-between items-center reps_info_status_container'>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>ALL TRIPS</p>
            <h1 className='font-semibold text-3xl'>{allTripsCount}</h1>
          </div>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>COMPLETED TRIPS</p>
            <h1 className='font-semibold text-3xl'>{completedTripsCount}</h1>
          </div>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>FAILED TRIPS</p>
            <h1 className='font-semibold text-3xl'>{failedTripsCount}</h1>
          </div>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>AVG. DELIVERY TIME</p>
            <h1 className='font-semibold text-3xl'>{averageDeliveryTime} mins</h1>
          </div>
        </div>
      </div>
      <div className='mt-6 flex justify-between items-center'>
        <p className='font-medium text-[20px]'>Trip History</p>
      </div>
      <div className='mt-6 flex justify-between items-center gap-4 border-b border-gray-border'>
        <ul className='flex justify-start no-scroll-bar grow'>
          {Object.values(ALL_TABS).map((value, index) => (
            <li
              role='button'
              key={index}
              onClick={() => setActiveTab(value)}
              className={`flex mr-6 last:mr-0 cursor-pointer py-4 whitespace-nowrap
            ${value === activeTab ? navStyles.active : navStyles.inactive}`}>
              {value}
            </li>
          ))}
        </ul>
      </div>
      <div className='flex gap-8 flex-wrap mt-8'>
        {loading ? (
          <div className='text-2xl leading-[40vh] text-center w-[80vw]'>Loading...</div>
        ) : trips?.length === 0 ? (
          <div className='text-2xl leading-[40vh] text-center w-[80vw]'>
            No {activeTab === ALL_TABS.ALL ? 'trips' : activeTab.toLocaleLowerCase()} found
          </div>
        ) : (
          <InfiniteScroll
            dataLength={trips?.length ?? 0}
            next={() => fetchMoreData()}
            hasMore={hasMore}
            loader={<p className='text-center mt-8'>Loading more trips...</p>}
            endMessage={<p className='text-center mt-8'>End of list</p>}>
            <div className='flex gap-8 flex-wrap w-full justify-between'>
              {trips?.map((trip: any) => (
                <div key={trip.id} className='w-full md:w-[47%]'>
                  <RepTripHistoryCard trip={trip} />
                </div>
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default TripHistory;
